import { useCallback, useMemo } from 'react';
import { atom, useAtom } from 'jotai';
import type { CustomArea } from '../../types/domain';

const customAreasAtom = atom<CustomArea[]>([]);

type UseCustomAreas = {
  customAreas: CustomArea[];
  visibleCustomAreas: CustomArea[];
  getCustomAreaByKey: (key: string) => CustomArea | undefined;
  setCustomAreas: (customAreas: CustomArea[]) => void;
  addCustomArea: (newCustomArea: CustomArea) => void;
  updateCustomArea: (
    key: string,
    changes: Partial<Omit<CustomArea, 'key'>>
  ) => void;
  deleteCustomArea: (key: string) => void;
  deleteAllCustomAreas: () => void;
};

const useCustomAreas = (): UseCustomAreas => {
  const [customAreas, setCustomAreas] = useAtom(customAreasAtom);

  const visibleCustomAreas = useMemo((): CustomArea[] => {
    return customAreas.filter((customArea: CustomArea): boolean => {
      return !customArea.hide;
    });
  }, [customAreas]);

  const getCustomAreaByKey = useCallback(
    (key: string): CustomArea | undefined => {
      return customAreas.find((customArea: CustomArea): boolean => {
        return customArea.key === key;
      });
    },
    [customAreas]
  );

  const addCustomArea = useCallback(
    (newCustomArea: CustomArea): void => {
      setCustomAreas((currentCustomAreas: CustomArea[]): CustomArea[] => {
        const isAlreadyLoaded = currentCustomAreas.some(
          (customArea: CustomArea): boolean => {
            return customArea.key === newCustomArea.key;
          }
        );

        return isAlreadyLoaded
          ? currentCustomAreas
          : [...currentCustomAreas, newCustomArea];
      });
    },
    [setCustomAreas]
  );

  const updateCustomArea = useCallback(
    (key: string, changes: Partial<Omit<CustomArea, 'key'>>): void => {
      setCustomAreas((currentCustomAreas: CustomArea[]): CustomArea[] => {
        return currentCustomAreas.map((customArea: CustomArea): CustomArea => {
          return customArea.key === key
            ? { ...customArea, ...changes }
            : customArea;
        });
      });
    },
    [setCustomAreas]
  );

  const deleteCustomArea = useCallback(
    (key: string): void => {
      setCustomAreas((currentCustomAreas: CustomArea[]): CustomArea[] => {
        return currentCustomAreas.filter((customArea: CustomArea): boolean => {
          return customArea.key !== key;
        });
      });
    },
    [setCustomAreas]
  );

  const deleteAllCustomAreas = useCallback((): void => {
    setCustomAreas([]);
  }, [setCustomAreas]);

  return {
    customAreas,
    visibleCustomAreas,
    getCustomAreaByKey,
    setCustomAreas,
    addCustomArea,
    updateCustomArea,
    deleteCustomArea,
    deleteAllCustomAreas,
  };
};

export { useCustomAreas };
